import { auth, db } from "./config";
import {
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signOut,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { store } from "../../store";
import { setUser, clearUser, setLoading } from "../../store/slices/authSlice";
import { functions } from "./config";
import { httpsCallable } from "firebase/functions";

const SESSION_TIMEOUT = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
let sessionTimer;
let debounceTimer;

const resetSessionTimer = () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    clearTimeout(sessionTimer);
    sessionTimer = setTimeout(() => {
      signOut(auth)
        .then(() => {
          console.log("Session expired. User logged out.");
        })
        .catch((error) => {
          console.error("Error signing out:", error);
        });
    }, SESSION_TIMEOUT);
  }, 1000);
};

const getUserData = async (user) => {
  const userRef = doc(db, "users", user.uid);
  const userData = await getDoc(userRef);

  if (!userData.exists()) {
    const newUserData = {
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      isAdmin: false,
      createdAt: serverTimestamp(),
    };
    await setDoc(userRef, newUserData);
    return { ...newUserData, uid: user.uid, emailVerified: user.emailVerified };
  }

  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    ...userData.data(),
  };
};

export const initializeAuth = async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    store.dispatch(setLoading(true));

    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userData = await getUserData(user);
        store.dispatch(setUser(userData));
        resetSessionTimer();
      } else {
        store.dispatch(clearUser());
        clearTimeout(sessionTimer);
        clearTimeout(debounceTimer);
      }
      store.dispatch(setLoading(false));
    });
  } catch (error) {
    console.error("Error initializing auth:", error);
    store.dispatch(setLoading(false));
    throw error;
  }
};

export const initializeSessionTracking = () => {
  const events = ["mousedown", "keydown", "touchstart", "scroll"];
  events.forEach((event) => {
    window.addEventListener(event, resetSessionTimer);
  });

  return () => {
    events.forEach((event) => {
      window.removeEventListener(event, resetSessionTimer);
    });
    clearTimeout(sessionTimer);
    clearTimeout(debounceTimer);
  };
};

export const verifyNFCToken = async (token) => {
  try {
    console.log("Verifying NFC token:", token);
    
    // Call the Firebase Function directly
    const verifyNfcTokenFunction = httpsCallable(functions, 'verifyNfcToken');
    const result = await verifyNfcTokenFunction({ token });
    const data = result.data;
    
    if (!data.success) {
      console.error("Token verification failed");
      return { success: false, error: "Invalid token" };
    }
    
    // Sign in with the custom token provided by the function
    try {
      await signInWithCustomToken(auth, data.customToken);
      return { success: true, userId: data.userId };
    } catch (authError) {
      console.error("Authentication error:", authError);
      return { success: false, error: authError.message };
    }
  } catch (error) {
    console.error("Token verification error:", error);
    return { success: false, error: error.message };
  }
};

// Add email/password authentication methods to maintain that functionality
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error("Email sign in error:", error);
    return { success: false, error: error.message };
  }
};

export const registerWithEmail = async (email, password, displayName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Set display name in Firebase Auth profile
    await updateProfile(userCredential.user, {
      displayName: displayName,
    });

    // Create user profile in Firestore
    const userRef = doc(db, "users", userCredential.user.uid);
    await setDoc(userRef, {
      email,
      displayName,
      photoURL: null,
      isAdmin: false,
      createdAt: serverTimestamp(),
    });
    
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error("Registration error:", error);
    return { success: false, error: error.message };
  }
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error("Password reset error:", error);
    return { success: false, error: error.message };
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error("Logout error:", error);
    return { success: false, error: error.message };
  }
};
